import { useState } from "react";
import { Info } from "lucide-react";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import {
  EVAL_CASES_API,
  EVAL_SCORE_MAX,
  type EvalCase,
  type EvalCaseCreate,
  type EvaluatorInput,
  type SelectableEvaluator,
} from "~/services/evals";
import { apiMutate } from "~/utils/apiClient";
import { POPOVER_IN_DIALOG_Z } from "./constants/page";
import { Field } from "./primitives/layout";
import { useEvalEvaluators } from "./useEvalApi";

/** A dataset as the catalog list returns it, narrowed to what the picker needs. */
export interface DatasetOption {
  id: string;
  title: string;
}

/**
 * Whether a companion value satisfies its input. Shared by the form's validity
 * gate and each field's `aria-invalid` so the two never disagree. An empty
 * optional value is valid; a required one is not. A number must be a whole
 * number at or above its bound.
 */
const isCompanionValueValid = (input: EvaluatorInput, raw: string): boolean => {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return !input.required;
  }
  if (input.input_type === "number") {
    const value = Number(trimmed);
    return Number.isInteger(value) && (input.min_value == null || value >= input.min_value);
  }
  return true;
};

interface CaseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasets: DatasetOption[];
  /** Tags already in use, offered so a new case joins an existing group. */
  availableTags: string[];
  onCreated: () => void;
}

/**
 * Creates an eval case, shaped by what a run needs rather than by the API's JSONB
 * columns. `inputs` and `expectations` are assembled on submit through the same
 * mapping the YAML importer performs, so a case created here is indistinguishable
 * from an imported one.
 *
 * Judges and assertions are separated because they do different things to the
 * result: a judge's score is what `score` averages, an assertion only flips
 * `passed`, and a case of assertions alone records no score — which the form says
 * rather than letting it be found in an empty column. At least one is required.
 */
export const CaseFormDialog = ({
  open,
  onOpenChange,
  datasets,
  availableTags,
  onCreated,
}: CaseFormDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [evaluators, setEvaluators] = useState<string[]>(["evaluate_text_response"]);
  // Companion values, keyed by the input's own key. One bag rather than a field
  // each, because which inputs exist is the catalog's call, not the form's.
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [threshold, setThreshold] = useState("");

  // The judge and assertion toggles — and the companion value each needs — are
  // the backend catalog, not a hand-kept copy, so the form cannot offer or ask
  // for anything the runner does not read.
  const { data: evaluatorCatalog } = useEvalEvaluators();
  const catalog = evaluatorCatalog ?? [];
  const judges = catalog.filter((evaluator) => evaluator.kind === "judge");
  const assertions = catalog.filter((evaluator) => evaluator.kind === "assertion");

  const judgeKeys = new Set(judges.map((evaluator) => evaluator.expectation_key));
  const hasJudge = evaluators.some((key) => judgeKeys.has(key));
  const selectedEvaluators = catalog.filter((evaluator) =>
    evaluators.includes(evaluator.expectation_key)
  );

  // The one field maps straight onto the case's `tags` list — a run selects
  // cases by tag containment, so this is what scopes a run to the case.
  const parsedTags = tagsInput
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

  const parsedThreshold = threshold.trim() === "" ? null : Number(threshold);
  const isThresholdValid = parsedThreshold == null || Number.isFinite(parsedThreshold);
  // Every companion value a selected evaluator asks for must satisfy its input;
  // a missing required one (e.g. a skill invocation with no skill named) would
  // fail the whole run the first time it is picked up.
  const companionsValid = selectedEvaluators.every(
    (evaluator) =>
      !evaluator.value_input ||
      isCompanionValueValid(evaluator.value_input, inputValues[evaluator.value_input.key] ?? "")
  );
  const isValid =
    name.trim().length > 0 &&
    query.trim().length > 0 &&
    datasetId !== "" &&
    parsedTags.length > 0 &&
    selectedEvaluators.length > 0 &&
    isThresholdValid &&
    companionsValid;

  const reset = () => {
    setName("");
    setDescription("");
    setQuery("");
    setDatasetId("");
    setTagsInput("");
    setEvaluators(["evaluate_text_response"]);
    setInputValues({});
    setThreshold("");
  };

  const { execute, isPending } = useAsyncMutation(
    async () => {
      const expectations: Record<string, unknown> = {};
      // Only `max_latency_seconds` is a case column today, but a value input
      // says where it goes, so the destination is read off the catalog.
      const caseFields: Record<string, number> = {};

      for (const evaluator of selectedEvaluators) {
        if (evaluator.presence === "flag") {
          expectations[evaluator.expectation_key] = true;
        }
        const input = evaluator.value_input;
        if (!input) {
          continue;
        }
        const raw = (inputValues[input.key] ?? "").trim();
        if (raw === "") {
          continue;
        }
        const value = input.input_type === "number" ? Number(raw) : raw;
        if (input.target === "case_field") {
          caseFields[input.key] = value as number;
        } else {
          expectations[input.key] = value;
        }
      }

      const body: EvalCaseCreate = {
        name: name.trim(),
        description: description.trim() || null,
        tags: parsedTags,
        dataset_ids: [datasetId],
        inputs: { query: query.trim() },
        expectations,
        pass_threshold: parsedThreshold,
        max_latency_seconds: caseFields.max_latency_seconds ?? null,
      };
      return apiMutate<EvalCase>(EVAL_CASES_API, "POST", body);
    },
    {
      errorMessage: "Could not create the case.",
      onSuccess: () => {
        reset();
        onCreated();
        onOpenChange(false);
      },
    }
  );

  const toggleEvaluator = (key: string) =>
    setEvaluators((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );

  const setInputValue = (key: string, value: string) =>
    setInputValues((current) => ({ ...current, [key]: value }));

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
      title="New eval case"
      description="A question, the dataset to ask it against, and what the judges should check."
      contentClassName="sm:max-w-2xl"
      primaryLabel="Create case"
      primaryPendingLabel="Creating…"
      primaryDisabled={!isValid}
      isPending={isPending}
      onPrimary={() => void execute()}
    >
      <div className="my-2 max-h-[60vh] overflow-y-auto pr-1">
        <div className="flex flex-col gap-5">
          <Field
            label="Name"
            htmlFor="new-case-name"
            hint="Snake case, matching the suite files — it is how the case is cited everywhere else."
          >
            <Input
              id="new-case-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="count_lung_cancer_cases"
              className="font-mono text-xs"
            />
          </Field>

          <Field
            label="Question"
            htmlFor="new-case-query"
            hint="Put to the agent verbatim, exactly as a user would type it."
          >
            <Textarea
              id="new-case-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              rows={3}
              placeholder="How many patients in the LUAD cohort have a KRAS mutation?"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Dataset"
              htmlFor="new-case-dataset"
              hint="The run resolves the agent's data access from this."
            >
              <Select value={datasetId} onValueChange={setDatasetId}>
                <SelectTrigger id="new-case-dataset" className="w-full">
                  <SelectValue placeholder="Select a dataset…" />
                </SelectTrigger>
                <SelectContent className={POPOVER_IN_DIALOG_Z}>
                  {datasets.map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id}>
                      {dataset.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Tags"
              htmlFor="new-case-tags"
              hint="Comma-separated. Runs select cases by tag, so these decide which runs cover the case."
            >
              <Input
                id="new-case-tags"
                value={tagsInput}
                onChange={(event) => setTagsInput(event.target.value)}
                placeholder="TCGA Data Retrieval, counts"
                list="new-case-tag-options"
              />
              <datalist id="new-case-tag-options">
                {availableTags.map((tag) => (
                  <option key={tag} value={tag} />
                ))}
              </datalist>
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <ToggleGroup
              legend={`Judges (score 1-${EVAL_SCORE_MAX})`}
              caption="One model call per judge, per iteration. Their scores set the case's score."
              evaluators={judges}
              selected={evaluators}
              onToggle={toggleEvaluator}
              inputValues={inputValues}
              onInputChange={setInputValue}
            />
            <ToggleGroup
              legend="Assertions (pass or fail)"
              caption="Free to run. They never affect the score; a false one only fails the case."
              evaluators={assertions}
              selected={evaluators}
              onToggle={toggleEvaluator}
              inputValues={inputValues}
              onInputChange={setInputValue}
            />
          </div>

          {evaluators.length === 0 && <p className="text-xs text-red-700">Turn on at least one.</p>}
          {evaluators.length > 0 && catalog.length > 0 && !hasJudge && (
            <p className="flex items-start gap-1.5 text-xs text-orange-700">
              <Info className="mt-px size-3.5 shrink-0" aria-hidden />
              Assertions only, so this case will record no score — it will pass or fail, and sit
              outside the score bands on the case-health grid.
            </p>
          )}

          <Field
            label="Pass threshold"
            htmlFor="new-case-threshold"
            hint="When set, every judge score must reach it and every assertion must pass. Blank means the assertions alone decide."
          >
            <Input
              id="new-case-threshold"
              type="number"
              step="0.1"
              min={1}
              max={4}
              value={threshold}
              onChange={(event) => setThreshold(event.target.value)}
              placeholder="None"
              aria-invalid={!isThresholdValid || undefined}
              className="sm:max-w-[12rem]"
            />
          </Field>
        </div>
      </div>
    </FormDialog>
  );
};

/**
 * One kind of evaluator, as a checkbox column. Both groups write into the same
 * `evaluators` list — the split is for the reader, not the payload. An evaluator
 * that needs a value shows the input inline once it is turned on, so the value
 * is authored beside the evaluator it belongs to.
 */
const ToggleGroup = ({
  legend,
  caption,
  evaluators,
  selected,
  onToggle,
  inputValues,
  onInputChange,
}: {
  legend: string;
  caption: string;
  evaluators: SelectableEvaluator[];
  selected: string[];
  onToggle: (key: string) => void;
  inputValues: Record<string, string>;
  onInputChange: (key: string, value: string) => void;
}) => (
  <fieldset className="flex flex-col gap-1.5">
    <legend className="text-sm font-medium text-foreground">{legend}</legend>
    <p className="text-xs text-muted-foreground">{caption}</p>
    <div className="mt-1 flex flex-col gap-3">
      {evaluators.map((evaluator) => {
        const isOn = selected.includes(evaluator.expectation_key);
        const input = evaluator.value_input;
        return (
          <div key={evaluator.expectation_key} className="flex flex-col gap-1.5">
            <div className="flex items-start gap-2">
              {/* `onCheckedChange`, not `onChange`: this is a Radix checkbox, which
                  renders a button and never emits a change event. And an explicit
                  `htmlFor` rather than a wrapping label, because a label only
                  activates the control it names — wrapping a Radix checkbox in one
                  leaves the text dead to the pointer. */}
              <Checkbox
                id={`new-case-${evaluator.expectation_key}`}
                checked={isOn}
                onCheckedChange={() => onToggle(evaluator.expectation_key)}
                className="mt-0.5 size-4"
              />
              <Label
                htmlFor={`new-case-${evaluator.expectation_key}`}
                className="cursor-pointer flex-col items-start gap-0 text-sm font-normal text-foreground"
              >
                {evaluator.label}
                <span className="text-xs text-muted-foreground">{evaluator.hint}</span>
              </Label>
            </div>
            {isOn && input && (
              <div className="ml-6">
                <CompanionInput
                  input={input}
                  value={inputValues[input.key] ?? ""}
                  onChange={(value) => onInputChange(input.key, value)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  </fieldset>
);

/** The value an evaluator needs, rendered inline once the evaluator is on. */
const CompanionInput = ({
  input,
  value,
  onChange,
}: {
  input: EvaluatorInput;
  value: string;
  onChange: (value: string) => void;
}) => (
  <Field label={input.label} htmlFor={`new-case-input-${input.key}`} hint={input.hint}>
    <Input
      id={`new-case-input-${input.key}`}
      type={input.input_type === "number" ? "number" : "text"}
      min={input.input_type === "number" && input.min_value != null ? input.min_value : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-invalid={!isCompanionValueValid(input, value) || undefined}
    />
  </Field>
);
